import { memo, useMemo } from 'react';

import { ExternalLink, Power, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import css from '../DomainsTable.module.css';

import { domainService } from 'v2/api/domainService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { DomainModel } from 'v2/types/DomainModel';
import { handleHttpError } from 'v2/utils';

interface DomainsTableActionsColumnProps {
  domain: DomainModel;
  onRevalidate: VoidFunction;
}

export const DomainsTableActionsColumn = memo<DomainsTableActionsColumnProps>(({ domain, onRevalidate }) => {
  const { trigger: triggerActivate } = useSWRMutation(['activateDomain', domain.id], domainService.activateDomain, {
    onSuccess: () => {
      toast.success('The domain has been activated successfully');
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerDelete } = useSWRMutation(['deleteDomain', domain.id], domainService.deleteDomain, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const disabled = useMemo(() => domain.isActive, [domain]);

  return (
    <div className={css.domainTableActions}>
      <Popconfirm
        message="Are you sure that you want to activate this domain?"
        onConfirm={triggerActivate}
        disabled={disabled}
      >
        <Tooltip title="Activate" placement="top">
          <IconButton color="success" disabled={disabled}>
            <Power width={20} height={20} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      <Tooltip title="Visit Domain" placement="top">
        <IconButton href={domain.domainName} target="_blank">
          <ExternalLink width={20} height={20} />
        </IconButton>
      </Tooltip>

      <Popconfirm
        message="Are you sure that you want to delete this domain?"
        onConfirm={triggerDelete}
        disabled={disabled}
      >
        <Tooltip title="Delete" placement="top">
          <IconButton color="error" disabled={disabled}>
            <Trash2 width={20} height={20} />
          </IconButton>
        </Tooltip>
      </Popconfirm>
    </div>
  );
});

DomainsTableActionsColumn.displayName = 'DomainsTableActionsColumn';
